import { Fragment } from "react";
import { loadFont as loadCaveat } from "@remotion/google-fonts/Caveat";
import moment from "moment";
import { AbsoluteFill, Easing, Img, OffthreadVideo, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { BoardingIntro } from "./BoardingIntro";
import { DepartureBoardIntro } from "./DepartureBoardIntro";
import { PassportIntro } from "./PassportIntro";
import { Glyph, IconTile, TEAL, tealShadow } from "./houseStyle";
import {
	BP_WORDS_OUT,
	CARDS_OUT,
	CD_CIRCLE,
	CD_DIVE,
	CD_NOTE,
	CD_ORBIT,
	CM_CIRCLE,
	CM_CUT,
	CM_DIVE,
	CM_DRAW,
	CM_GHOST,
	CM_NOTE,
	CM_OUT,
	CM_OUTLINE,
	CM_WORDS_OUT,
	CUT_IN,
	CUT_OUT,
	CZ_CIRCLE,
	CZ_NOTE,
	CZ_SHRINK,
	CZ_WINDOW,
	CZ_WORDS_OUT,
	CZ_ZOOM,
	DRAW_IN,
	DRAW_OUT,
	FLAG_SLAP,
	F_DIVE,
	F_DRAW,
	F_FLIP,
	F_GHOST,
	F_GROW,
	F_SHRINK,
	F_WORDS_OUT,
	GHOST_OUT,
	G_DIVE,
	G_DRAW,
	G_EXPAND,
	G_GHOST,
	G_MOONS_OUT,
	G_POP,
	G_WORDS_OUT,
	IntroCameraContext,
	LngLat,
	OUTLINE_IN,
	OUTLINE_OUT,
	PF_WORDS_OUT,
	PP_WORDS_OUT,
	SHRINK_IN,
	SHRINK_OUT,
	TS_WORDS_OUT,
	WINDOW_IN,
	WINDOW_OUT,
	WORDS_OUT,
	applyIntroCamera,
	introCameraAt,
	introCameraNumbers,
	introGeometry,
	isoOf,
} from "./introCamera";
import { bowedArc, hashSeed, layoutCards, markerStroke, pointAlong, scribbleLoop, smoothed, spread } from "./introDrawing";
import { IntroConfig, IntroHeader, IntroStyle, introOverhang, vertexFractions } from "./introPlan";
import { glyphFor } from "./puffyGlyphs";
import { isVideoMedia, mediaStartAt, mediaUrl } from "./reelMedia";

/**
 * The reel's opening when it starts on a clip instead of on the globe. See `introPlan.ts` for the two
 * styles. Everything here is driven by the frame number: the title used to be animated with
 * framer-motion (still commented out in ReelTitle), which runs on wall-clock time and so renders as
 * a still in Remotion -- which is why the classic title card has no entrance at all.
 *
 * The map is under the clip the whole time, and the camera is ALREADY where the reveal needs it
 * before the clip opens. That is the transition: nothing cuts to the map, the map was there.
 */

// Only the weight the captions use, latin only: an unqualified loadFont fetches every weight and
// subset during a render that should not depend on the network being kind.
const { fontFamily: handwriting } = loadCaveat("normal", { weights: ["700"], subsets: ["latin"] });

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/** When each style's header leaves. clip-pin's goes as the clip starts to shrink. */
const WORDS_WINDOW: Partial<Record<IntroStyle, number[]>> = {
	"route-cut": WORDS_OUT,
	"globe-bubble": G_WORDS_OUT,
	"card-flip": F_WORDS_OUT,
	"circle-zoom": CZ_WORDS_OUT,
	"circle-match": CM_WORDS_OUT,
	"passport-flip": PF_WORDS_OUT,
	"passport-photos": PP_WORDS_OUT,
	"boarding-pass": BP_WORDS_OUT,
	"ticket-stack": TS_WORDS_OUT,
};

const CARD_TILT = [-7, 5, -4, 8, -6, 4];
const MAX_CARDS = 5;

const ClipIntro = ({
	map,
	style,
	config,
	introFrames,
	currentTrip,
	paths,
	reelData,
	stop,
	endAltitude,
	endPitch,
	endBearing,
	brandColor,
	scale,
}: {
	map: any;
	style: Exclude<IntroStyle, "classic">;
	config: IntroConfig;
	introFrames: number;
	currentTrip: any;
	paths: any[];
	reelData: any[];
	stop: LngLat;
	endAltitude: number;
	endPitch: number;
	endBearing: number;
	brandColor: string;
	scale: number;
}) => {
	const frame = useCurrentFrame();
	const { fps, width, height } = useVideoConfig();
	const px = (n: number) => n * scale;

	// The trip in geographic space, and the camera on this frame: both from introCamera.ts, which the
	// tile warm-up reads too.
	const geometry = introGeometry({ style, paths, reelData, currentTrip });
	const { legs, legVehicle, flat, stopsGeo, rings } = geometry;

	// Mapbox is built before its container has a size and falls back to 400x300 until a ResizeObserver
	// catches up -- a race, measured both ways on the same frame. The composition resizes it on load;
	// this is the backstop, because every coordinate projected below is only as right as the viewport.
	if (map && (map.transform?.width !== width || map.transform?.height !== height)) map.resize();

	// The camera belongs to MarkerSequence from `introFrames` on; the overlay runs on past that point,
	// and must not keep writing a camera the stop is already driving.
	const camera: IntroCameraContext = {
		style,
		config,
		introFrames,
		stop,
		endAltitude,
		endPitch,
		endBearing,
		width,
		height,
		scale,
		geometry,
		fit: map ? (bounds, padding) => map.cameraForBounds?.(bounds, { padding }) : undefined,
	};
	const numbers = introCameraNumbers(camera);
	const { layout, stageX, stageY, bubbleR, flipW, flipH, flipCx, flipCy, circleHeld, circleClose, czAltitude, cmAltitude, circleAt, cdRx, cdAltitude } = numbers;
	if (map && frame < introFrames) applyIntroCamera(map, introCameraAt(frame, camera, numbers), camera);

	const project = (c: number[] | LngLat) => {
		if (!map) return [0, 0];
		const p = map.project(Array.isArray(c) ? (c as [number, number]) : [c.lng, c.lat]);
		return [p.x, p.y];
	};

	// ---- words -------------------------------------------------------------------------------------
	const nights = moment(currentTrip?.endDate).diff(currentTrip?.startDate, "days") || currentTrip?.duration;
	const stopCount = currentTrip?.numberOfDestinations || currentTrip?.destinations?.length;
	// One of each is singular: a one-night stay or a two-stop day is not "1 nights" or "1 stops".
	const nightsText = nights === 1 ? "1 night" : `${nights} nights`;
	const stopsText = stopCount === 1 ? "1 stop" : `${stopCount} stops`;
	const kicker = config.kicker ?? [nights ? nightsText : null, stopCount ? stopsText : null].filter(Boolean).join("  ·  ");
	const title: string = String(config.title ?? currentTrip?.name ?? "");
	const look = config.look ?? "stippl";
	const stippl = look === "stippl";
	const wordsWindow = WORDS_WINDOW[style] ?? [SHRINK_IN - 8, SHRINK_IN + 6];
	const wordsOut = interpolate(frame, wordsWindow, [0, 1], {
		...clamp,
		easing: Easing.in(Easing.cubic),
	});
	const kickerIn = spring({ frame: frame + 2, fps, config: { damping: 18, stiffness: 140 } });

	// Letter by letter, each on its own spring and tipped the opposite way to its neighbour -- only a
	// little in the house style, where type stands up straight. Frame 0 is the thumbnail, so the first
	// letters are already landing on it rather than fading up from nothing.
	const tip = stippl ? 4 : 14;
	// Each letter is its own inline block so it can drop in on its own, and a line may break between any
	// two of those: "Amsterdam bar crawl" wrapped as "Amsterdam b / ar crawl". So the letters sit in words
	// that do not break, and a line only breaks at the spaces. `at` counts every character, spaces
	// included, so the drop-in keeps the timing it had.
	let at = 0;
	const letters = title.split(" ").map((word, w, words) => {
		const chars = Array.from(word).map((ch) => {
			const i = at++;
			const s = spring({ frame: frame + 6 - i * 1.6, fps, config: { damping: 11, stiffness: 190 } });
			return (
				<span
					key={i}
					style={{
						display: "inline-block",
						transform: `translateY(${px(90) * (1 - s)}px) rotate(${(i % 2 ? 1 : -1) * tip * (1 - s)}deg)`,
						opacity: Math.min(1, s * 1.6),
						whiteSpace: "pre",
					}}
				>
					{ch}
				</span>
			);
		});
		at++;
		return (
			<Fragment key={w}>
				<span style={{ display: "inline-block", whiteSpace: "nowrap" }}>{chars}</span>
				{w < words.length - 1 ? " " : null}
			</Fragment>
		);
	});

	// The flag, slapped on like a sticker: it arrives big and settles with an overshoot, tilted. One
	// per country, fanned, up to three.
	const flags = (currentTrip?.countries || [])
		.map((c: any) => isoOf(c?.name)?.toLowerCase())
		.filter(Boolean)
		.slice(0, 3) as string[];
	const flagStack = flags.map((code: string, k: number) => {
		const s = spring({ frame: frame - (FLAG_SLAP + k * 5), fps, config: { damping: 9, stiffness: 170 } });
		const tilt = 9 - k * 8;
		return (
			<div
				key={code}
				style={{
					position: "absolute",
					right: px(76 + k * 64),
					top: px(170 + k * 18),
					width: px(172),
					height: px(116),
					borderRadius: px(12),
					border: `${px(7)}px solid #fff`,
					overflow: "hidden",
					boxShadow: `0 ${px(6 + 8 * s)}px ${px(22)}px rgba(0,0,0,${0.18 + 0.17 * s})`,
					transform: `rotate(${tilt + 16 * (1 - s)}deg) scale(${2.6 - 1.6 * s})`,
					opacity: Math.min(1, s * 3),
					zIndex: 3 - k,
				}}
			>
				<Img src={`https://flagcdn.com/w320/${code}.png`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
			</div>
		);
	});

	// ---- house-style headers ----------------------------------------------------------------------
	// The flag is the website's round badge: white ring, soft teal shadow. Never pinned to the title --
	// on its corner it covered the last letter, and a longer name would run into it anyway.
	const flagBadge = (code: string, k: number, size: number, ring: number, at = FLAG_SLAP) => {
		const s = spring({ frame: frame - (at + k * 5), fps, config: { damping: 9, stiffness: 170 } });
		return (
			<div
				key={code}
				style={{
					flexShrink: 0,
					marginLeft: k ? -size * 0.3 : 0,
					width: size,
					height: size,
					borderRadius: "50%",
					border: `${ring}px solid #fff`,
					boxSizing: "border-box",
					overflow: "hidden",
					background: "#fff",
					boxShadow: tealShadow(px(4), px(16), 0.3),
					transform: `scale(${s}) rotate(${-24 * (1 - s)}deg)`,
					zIndex: 3 - k,
				}}
			>
				<Img src={`https://flagcdn.com/w320/${code}.png`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
			</div>
		);
	};
	const flagRow = (size: number, ring: number, at?: number) =>
		flags.length ? <div style={{ display: "flex", alignItems: "center" }}>{flags.map((c: string, k: number) => flagBadge(c, k, size, ring, at))}</div> : null;

	/** A Puffy glyph as an inline element, for use inside HTML rather than inside the overlay SVG. */
	const inlineGlyph = (name: string, size: number, color: string) => (
		<svg width={size} height={size} viewBox={`${-size / 2} ${-size / 2} ${size} ${size}`} style={{ flexShrink: 0 }}>
			<Glyph name={name} size={size} color={color} />
		</svg>
	);

	const facts: { glyph?: string; text: string }[] = config.kicker
		? [{ text: config.kicker }]
		: ([nights ? { glyph: "moon", text: nightsText } : null, stopCount ? { glyph: "map-marker", text: stopsText } : null].filter(
				Boolean
			) as { glyph?: string; text: string }[]);
	const chips = facts.map((f, k) => {
		const s = spring({ frame: frame - (10 + k * 5), fps, config: { damping: 12, stiffness: 170 } });
		return (
			<div
				key={k}
				style={{
					display: "flex",
					alignItems: "center",
					gap: px(10),
					height: px(66),
					padding: `0 ${px(28)}px 0 ${px(f.glyph ? 18 : 28)}px`,
					borderRadius: px(33),
					background: "#fff",
					color: TEAL,
					fontSize: px(30),
					fontWeight: 600,
					textShadow: "none",
					boxShadow: tealShadow(px(4), px(16)),
					transform: `translateY(${px(24) * (1 - s)}px) scale(${0.7 + 0.3 * s})`,
					opacity: Math.min(1, s * 2),
				}}
			>
				{f.glyph ? inlineGlyph(f.glyph, px(38), brandColor) : null}
				{f.text}
			</div>
		);
	});

	const bigTitle = (size: number, color = "#fff", extra: React.CSSProperties = {}) => (
		<div
			style={{
				fontSize: size,
				fontWeight: 700,
				lineHeight: 1.05,
				letterSpacing: "-0.02em",
				color,
				textShadow: color === "#fff" ? `0 ${px(2)}px ${px(18)}px rgba(15,62,74,.35)` : "none",
				...extra,
			}}
		>
			{letters}
		</div>
	);
	// A landscape frame gives the words a column rather than the width, so the title comes down a size.
	const titleSize = (base: number) => px((title.length > 12 ? base * 0.76 : base) * (layout.landscape ? 0.84 : 1));

	// The website's secondary grey for the line under a title on a white surface (design rules, 4.80
	// on bgLight), and its four pastel icon tiles, measured off stippl.io's `.icon-badge`.
	const MUTED = "#58757B";
	const TILE: Record<string, { bg: string; fg: string }> = {
		green: { bg: "#e8f9f4", fg: "#2e9e72" },
		orange: { bg: "#fff8ec", fg: "#f5a623" },
		purple: { bg: "#f5f0ff", fg: "#7c3aed" },
		pink: { bg: "#fff0f5", fg: "#e84d7e" },
	};

	const header: IntroHeader = config.header ?? "stack";
	let stipplWords: React.ReactNode = null;

	if (header === "accent") {
		// The website's hero heading, "Let's plan it.", where one word carries a green underline. Green
		// TEXT does not survive a film -- on the site it sits on white; here it measured as a smear on
		// grey sky -- so the words stay white and only the underline is green, drawn in once the title
		// has landed.
		const when = moment(currentTrip?.startDate);
		const eyebrow = [stopCount ? `${stopCount} stops` : null, when.isValid() ? when.format("MMM YYYY") : null].filter(Boolean).join("  ·  ");
		const eyebrowIn = spring({ frame: frame + 2, fps, config: { damping: 18, stiffness: 140 } });
		const lineIn = spring({ frame: frame - 8, fps, config: { damping: 14, stiffness: 150 } });
		const underline = interpolate(frame, [18, 36], [0, 1], { ...clamp, easing: Easing.inOut(Easing.cubic) });
		stipplWords = (
			<div style={{ position: "absolute", top: px(236), left: px(72), right: px(72), fontFamily: "Poppins", color: "#fff" }}>
				<div style={{ display: "flex", alignItems: "center", gap: px(16), opacity: eyebrowIn, transform: `translateY(${px(20) * (1 - eyebrowIn)}px)` }}>
					{flagRow(px(54), px(4), 2)}
					<div
						style={{
							fontSize: px(28),
							fontWeight: 700,
							letterSpacing: px(5),
							textTransform: "uppercase",
							opacity: 0.9,
							textShadow: `0 ${px(1)}px ${px(10)}px rgba(15,62,74,.4)`,
						}}
					>
						{eyebrow}
					</div>
				</div>
				{bigTitle(titleSize(156), "#fff", { marginTop: px(12) })}
				{nights ? (
					<div
						style={{
							fontSize: px(92),
							fontWeight: 700,
							lineHeight: 1.1,
							letterSpacing: "-0.02em",
							textShadow: `0 ${px(2)}px ${px(14)}px rgba(15,62,74,.35)`,
							opacity: Math.min(1, lineIn * 2),
							transform: `translateY(${px(30) * (1 - lineIn)}px)`,
						}}
					>
						in{" "}
						<span style={{ position: "relative", display: "inline-block" }}>
							{nightsText}
							<span
								style={{
									position: "absolute",
									left: 0,
									bottom: px(4),
									height: px(12),
									width: `${100 * underline}%`,
									borderRadius: px(6),
									background: brandColor,
								}}
							/>
						</span>
						.
					</div>
				) : null}
			</div>
		);
	} else if (header === "card") {
		// The header on a white surface, the way the app shows a trip: legible on any clip, whether or
		// not it has calm sky where the words go.
		const s = spring({ frame: frame + 4, fps, config: { damping: 14, stiffness: 150 } });
		stipplWords = (
			<div
				style={{
					position: "absolute",
					top: px(214),
					left: px(56),
					right: px(56),
					display: "flex",
					alignItems: "center",
					gap: px(26),
					padding: `${px(28)}px ${px(34)}px ${px(28)}px ${px(28)}px`,
					borderRadius: px(36),
					background: "#fff",
					boxShadow: tealShadow(px(14), px(44), 0.28),
					fontFamily: "Poppins",
					transform: `translateY(${px(-40) * (1 - s)}px) scale(${0.92 + 0.08 * s})`,
					opacity: Math.min(1, s * 2),
				}}
			>
				{flagRow(px(116), px(6), 4)}
				<div style={{ minWidth: 0 }}>
					{bigTitle(titleSize(96), TEAL)}
					<div style={{ display: "flex", alignItems: "center", gap: px(22), marginTop: px(8), color: MUTED, fontSize: px(28), fontWeight: 500 }}>
						{facts.map((f, k) => (
							<div key={k} style={{ display: "flex", alignItems: "center", gap: px(8) }}>
								{f.glyph ? inlineGlyph(f.glyph, px(32), brandColor) : null}
								{f.text}
							</div>
						))}
					</div>
				</div>
			</div>
		);
	} else if (header === "poster") {
		// Centred, flag first and big: a poster rather than a caption.
		stipplWords = (
			<div
				style={{
					position: "absolute",
					top: px(180),
					left: px(40),
					right: px(40),
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					fontFamily: "Poppins",
				}}
			>
				{flagRow(px(128), px(7), 0)}
				{bigTitle(titleSize(150), "#fff", { marginTop: px(12), textAlign: "center" })}
				<div style={{ display: "flex", alignItems: "center", gap: px(14), marginTop: px(16) }}>{chips}</div>
			</div>
		);
	} else if (header === "stats") {
		// Numbers first: the trip counted, each in one of the website's pastel icon tiles.
		const byVehicle = (legVehicle as (string | undefined)[]).reduce<Record<string, number>>((acc, v) => {
			const g = glyphFor(v);
			acc[g] = (acc[g] || 0) + 1;
			return acc;
		}, {});
		const RIDE: Record<string, { label: string; tone: string }> = {
			plane: { label: "flights", tone: "purple" },
			boat: { label: "ferries", tone: "orange" },
			car: { label: "drives", tone: "pink" },
			train: { label: "train rides", tone: "purple" },
			bus: { label: "bus rides", tone: "orange" },
			hike: { label: "walks", tone: "green" },
		};
		const rides = Object.entries(byVehicle)
			.sort((a, b) => b[1] - a[1])
			.slice(0, 2)
			.map(([g, n]) => ({ glyph: g, n, label: n === 1 ? RIDE[g].label.replace(/s$/, "").replace(/ferrie$/, "ferry") : RIDE[g].label, tone: RIDE[g].tone }));
		const stats = [
			nights ? { glyph: "moon", n: nights, label: nights === 1 ? "night" : "nights", tone: "green" } : null,
			...(rides.length ? rides : stopCount ? [{ glyph: "map-marker", n: stopCount, label: "stops", tone: "orange" }] : []),
		]
			.filter(Boolean)
			.slice(0, 3) as { glyph: string; n: number; label: string; tone: string }[];
		stipplWords = (
			<div style={{ position: "absolute", top: px(236), left: px(72), right: px(72), fontFamily: "Poppins" }}>
				<div style={{ display: "flex", alignItems: "center", gap: px(22) }}>
					{flagRow(px(96), px(6), 4)}
					{bigTitle(titleSize(140))}
				</div>
				<div style={{ display: "flex", gap: px(14), marginTop: px(24) }}>
					{stats.map((st, k) => {
						const s = spring({ frame: frame - (8 + k * 6), fps, config: { damping: 12, stiffness: 170 } });
						// The number counts up once its tile has landed.
						const n = Math.round(st.n * interpolate(frame, [12 + k * 6, 30 + k * 6], [0, 1], { ...clamp, easing: Easing.out(Easing.cubic) }));
						const tone = TILE[st.tone];
						return (
							<div
								key={k}
								style={{
									display: "flex",
									alignItems: "center",
									gap: px(14),
									padding: `${px(14)}px ${px(24)}px ${px(14)}px ${px(14)}px`,
									borderRadius: px(28),
									background: "#fff",
									boxShadow: tealShadow(px(6), px(20), 0.2),
									transform: `translateY(${px(26) * (1 - s)}px) scale(${0.7 + 0.3 * s})`,
									opacity: Math.min(1, s * 2),
								}}
							>
								<div
									style={{
										width: px(66),
										height: px(66),
										borderRadius: px(20),
										background: tone.bg,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									{inlineGlyph(st.glyph, px(40), tone.fg)}
								</div>
								<div>
									<div style={{ fontSize: px(44), fontWeight: 700, lineHeight: 1, color: TEAL }}>{n}</div>
									<div style={{ fontSize: px(22), fontWeight: 500, color: MUTED, marginTop: px(4) }}>{st.label}</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	} else {
		// stack: big title, then the flag leading a row of fact pills.
		stipplWords = (
			<div style={{ position: "absolute", top: px(250), left: px(72), right: px(72), fontFamily: "Poppins", color: "#fff" }}>
				{bigTitle(titleSize(164))}
				<div style={{ display: "flex", alignItems: "center", gap: px(14), marginTop: px(22) }}>
					{flagRow(px(72), px(5))}
					{chips}
				</div>
			</div>
		);
	}

	// Every header is laid out against the frame's edges; in landscape those edges are the words' column.
	// AbsoluteFill also sets `width: 100%`, which wins over `right`: without `width: auto` the column was
	// the whole frame and a long title ran on under whatever stood right of it.
	const column = layout.landscape ? { right: width * (1 - layout.words), width: "auto" } : {};
	const words = stippl ? (
		<AbsoluteFill style={{ opacity: 1 - wordsOut, transform: `translateY(${px(-70) * wordsOut}px)`, ...column }}>{stipplWords}</AbsoluteFill>
	) : (
		<AbsoluteFill style={{ opacity: 1 - wordsOut, transform: `translateY(${px(-70) * wordsOut}px)`, ...column }}>
			<div
				style={{
					position: "absolute",
					top: px(260),
					left: px(72),
					right: px(72),
					fontFamily: "Poppins",
					color: "#fff",
					textShadow: `0 ${px(2)}px ${px(18)}px rgba(0,0,0,.35)`,
				}}
			>
				<div
					style={{
						fontSize: px(34),
						fontWeight: 600,
						letterSpacing: px(5),
						textTransform: "uppercase",
						opacity: kickerIn,
						transform: `translateY(${px(24) * (1 - kickerIn)}px)`,
					}}
				>
					{kicker}
				</div>
				<div style={{ fontSize: px((title.length > 12 ? 128 : 176) * (layout.landscape ? 0.84 : 1)), fontWeight: 800, lineHeight: 1.02, letterSpacing: px(-4), marginTop: px(10) }}>
					{letters}
				</div>
			</div>
			{flagStack}
		</AbsoluteFill>
	);

	// A standing clip in a landscape frame shows only a band of itself. For circle-match the band is
	// moved to the subject: with the crop anchored at the subject's own height, it stays at that height
	// in the frame, so `circleAt` still says where to draw the circle.
	const clipAnchor = layout.landscape && style === "circle-match" ? `50% ${circleAt.y * 100}%` : "50% 50%";
	const clip = config.clip ? (
		<OffthreadVideo
			src={mediaUrl(config.clip)}
			startFrom={Math.round(mediaStartAt(config.clip) * fps)}
			muted
			style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: clipAnchor }}
		/>
	) : null;

	// Top scrim for the words: curated clips are mostly bright sky exactly where the title goes.
	const scrim = (
		<AbsoluteFill
			style={{
				// Teal rather than black in the house style: a black scrim greys the film, a teal one reads as ours.
				background: stippl
					? "linear-gradient(to bottom, rgba(15,62,74,.62) 0%, rgba(15,62,74,.22) 32%, rgba(15,62,74,0) 50%)"
					: "linear-gradient(to bottom, rgba(0,0,0,.5) 0%, rgba(0,0,0,.18) 30%, rgba(0,0,0,0) 46%)",
			}}
		/>
	);

	// The passports draw their own page over the map and lay the header over it (PassportIntro.tsx).
	if (style === "passport-flip" || style === "passport-photos") {
		return (
			<PassportIntro
				style={style}
				frame={frame}
				width={width}
				height={height}
				px={px}
				layout={layout}
				reelData={reelData}
				currentTrip={currentTrip}
				nightsText={nightsText}
				handwriting={handwriting}
				words={words}
				scrim={scrim}
			/>
		);
	}

	// The pass and the tickets stand under the header like the passports (BoardingIntro.tsx); the boards
	// fill the frame and are their own title (DepartureBoardIntro.tsx).
	if (style === "boarding-pass" || style === "ticket-stack") {
		return (
			<BoardingIntro
				style={style}
				frame={frame}
				width={width}
				height={height}
				px={px}
				layout={layout}
				reelData={reelData}
				currentTrip={currentTrip}
				paths={paths}
				config={config}
				nightsText={nightsText}
				stopsText={stopsText}
				brandColor={brandColor}
				stopXY={project(stop)}
				words={words}
			/>
		);
	}
	if (style === "where-to-next" || style === "departure-board") {
		return (
			<DepartureBoardIntro
				style={style}
				frame={frame}
				width={width}
				height={height}
				px={px}
				reelData={reelData}
				currentTrip={currentTrip}
				nightsText={nightsText}
				stopsText={stopsText}
				brandColor={brandColor}
			/>
		);
	}

	// ---- the sketch every map style draws -------------------------------------------------------
	// A point Mapbox cannot place this frame comes back as NaN; holding the last good one keeps the
	// line and every timing derived from its length finite.
	const place = (pts: number[][]) => {
		const out: number[][] = [];
		pts.map(project).forEach((p) => {
			const ok = isFinite(p[0]) && isFinite(p[1]);
			out.push(ok ? p : out[out.length - 1] || [width / 2, height / 2]);
		});
		return out;
	};
	const len = (xy: number[][]) => xy.slice(1).reduce((a, p, i) => a + Math.hypot(p[0] - xy[i][0], p[1] - xy[i][1]), 0);
	const pathOf = (xy: number[][], close = false) => xy.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ") + (close ? " Z" : "");

	// The sketch: flights bow, everything else is smoothed through its own points. Built in screen
	// space every frame, from the projected ends, so it still sits on the map as the camera moves.
	const stopsXY = place(stopsGeo);
	const centre = stopsXY.length
		? [stopsXY.reduce((a, p) => a + p[0], 0) / stopsXY.length, stopsXY.reduce((a, p) => a + p[1], 0) / stopsXY.length]
		: [width / 2, height / 2];
	const xy: number[][] = [];
	const stopAt: number[] = [];
	const legAt: number[] = [];
	legs.forEach((coords, i) => {
		const a = stopsXY[i];
		const b = stopsXY[i + 1];
		const drawn = legVehicle[i] === "plane" || coords.length === 2 ? bowedArc(a, b, centre, 0.2, px(120)) : smoothed(place(coords));
		stopAt.push(xy.length);
		drawn.forEach((p) => {
			xy.push(p);
			legAt.push(i);
		});
	});
	if (xy.length) stopAt.push(xy.length - 1);

	const d = pathOf(xy);
	const fr = vertexFractions(xy);
	const L = len(xy);
	const [drawIn, drawOut] = style === "globe-bubble" ? G_DRAW : style === "card-flip" ? F_DRAW : style === "circle-match" ? CM_DRAW : [DRAW_IN, DRAW_OUT];
	const draw = interpolate(frame, [drawIn, drawOut], [0, 1], { ...clamp, easing: Easing.inOut(Easing.sin) });
	/** The frame the line reaches a given vertex. */
	const reach = (vi: number) => drawIn + (drawOut - drawIn) * (isFinite(fr[vi]) ? fr[vi] : 1);

	// A stop: the app's marker at small size -- a green dot in a white ring. Every stop the same size,
	// the first included; the first is only told apart by pulsing, not by being bigger. It stays when
	// the rest of the sketch fades, and hands over to the real marker at the end of the dive.
	const [fx, fy] = stopsXY[0] || [width / 2, height / 2];
	const handOver = interpolate(frame, [introFrames, introFrames + introOverhang(style)], [1, 0], clamp);
	const dotR = px(13);
	const dot = (key: string | number, x: number, y: number, k: number, opacity = 1) => (
		<g key={key} transform={`translate(${x} ${y}) scale(${k})`} opacity={opacity}>
			<circle r={dotR + px(7)} cy={px(2)} fill="rgba(15,62,74,.28)" />
			<circle r={dotR + px(5)} fill="#fff" />
			<circle r={dotR} fill={brandColor} />
		</g>
	);
	const pulse = (at: number) => {
		const q = interpolate(frame, [at, at + 24], [0, 1], { ...clamp, easing: Easing.out(Easing.cubic) });
		return q > 0 && q < 1 ? (
			<circle key={at} cx={fx} cy={fy} r={dotR + px(6) + px(90) * q} fill="none" stroke={brandColor} strokeWidth={px(4)} strokeOpacity={1 - q} />
		) : null;
	};

	const dotGap = px(27);

	// The route as the house style draws it: a dotted line of round dots, revealed by a solid stroke in
	// a mask. `id` keeps the masks of different styles apart.
	// Nothing before the line starts: a round-capped reveal stroke at zero length still paints a dot at
	// the start, and on the globe, with the first stop on the far side, that dot sat on the rim.
	const dottedRoute = (id: string) =>
		draw <= 0.001 ? null : (
			<>
				<defs>
					<mask id={id} maskUnits="userSpaceOnUse" x={0} y={0} width={width} height={height}>
						<path
							d={d}
							fill="none"
							stroke="#fff"
							strokeWidth={px(48)}
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeDasharray={L}
							strokeDashoffset={L * (1 - draw)}
						/>
					</mask>
				</defs>
				<g mask={`url(#${id})`}>
					<path
						d={d}
						fill="none"
						stroke="rgba(15,62,74,.3)"
						strokeWidth={px(17)}
						strokeLinecap="round"
						strokeDasharray={`0 ${dotGap}`}
						transform={`translate(0 ${px(2)})`}
					/>
					<path d={d} fill="none" stroke="#fff" strokeWidth={px(12)} strokeLinecap="round" strokeDasharray={`0 ${dotGap}`} />
				</g>
			</>
		);
	const laterStops = () =>
		stopAt.slice(1).map((vi, i) => {
			const [x, y] = xy[vi] || [0, 0];
			return dot(i, x, y, spring({ frame: frame - reach(vi), fps, config: { damping: 9, stiffness: 220 } }));
		});
	const firstStop = () =>
		dot("first", fx, fy, spring({ frame: frame - drawIn + 4, fps, config: { damping: 9, stiffness: 220 } }) * (0.7 + 0.3 * handOver), handOver);
	/** The vehicle on the tip of the line, in the website's floating icon tile. */
	const vehicleTile = () => {
		const head = pointAlong(xy, draw * L);
		const glyph = glyphFor(legVehicle[legAt[head.index] ?? 0]);
		const rideIn = spring({ frame: frame - drawIn, fps, config: { damping: 12, stiffness: 180 } });
		const rideOut = interpolate(frame, [drawOut, drawOut + 8], [1, 0], { ...clamp, easing: Easing.in(Easing.back(2)) });
		const ride = Math.max(0, rideIn * rideOut);
		if (ride <= 0.01) return null;
		return (
			<g transform={`translate(${head.at[0]} ${head.at[1]}) scale(${ride})`}>
				<IconTile size={px(80)} glyph={glyph} color={brandColor} facing={Math.abs(head.heading) > 90 ? -1 : 1} />
			</g>
		);
	};

	// ---- route-cut ---------------------------------------------------------------------------------
	if (style === "route-cut") {
		const ringsXY = rings.map((r) => smoothed(place(r), 4));
		const countryD = ringsXY.map((r) => pathOf(r, true)).join(" ");
		// Dashing restarts on every subpath, so the longest ring sets the pace and the islands finish early.
		const Lc = Math.max(1, ...ringsXY.map(len));
		const outline = interpolate(frame, [OUTLINE_IN, OUTLINE_OUT], [0, 1], { ...clamp, easing: Easing.inOut(Easing.cubic) });
		const hasCountry = ringsXY.length > 0;

		// The window: the country's inside turns from film into map. Then the tear: its edge moves out
		// until it is wider than the frame, accelerating, because a tear does. Without a country the
		// route itself is the window, a slit that opens.
		const windowOpen = interpolate(frame, [WINDOW_IN, WINDOW_OUT], [0, 1], { ...clamp, easing: Easing.inOut(Easing.quad) });
		const diag = Math.hypot(width, height);
		const cut = interpolate(frame, [CUT_IN, CUT_OUT], [0, diag * 1.4], { ...clamp, easing: Easing.in(Easing.cubic) });
		const hole = hasCountry ? countryD : d;
		const push = interpolate(frame, [0, CUT_OUT], [1.02, 1.2], { ...clamp, easing: Easing.in(Easing.quad) });
		const ghost = interpolate(frame, GHOST_OUT, [1, 0], clamp);
		const rim = px(10);

		// The vehicle riding the tip of the line while it is being drawn: a Puffy glyph. It never turns --
		// Puffy is drawn at an angle of its own, and a turned icon stops reading as the icon -- it only
		// faces the way it is going.
		const head = pointAlong(xy, draw * L);
		const headLeg = legAt[head.index] ?? 0;
		const glyph = glyphFor(legVehicle[headLeg]);
		const rideIn = spring({ frame: frame - DRAW_IN, fps, config: { damping: 12, stiffness: 180 } });
		const rideOut = interpolate(frame, [DRAW_OUT, DRAW_OUT + 8], [1, 0], { ...clamp, easing: Easing.in(Easing.back(2)) });
		const ride = Math.max(0, rideIn * rideOut);
		const facing = Math.abs(head.heading) > 90 ? -1 : 1;

		// One photograph per stop that has one, coming out of its dot when the line gets there and hanging
		// in a column beside the country. House style: a round photo with a white ring and the name on a
		// pill, the website's destination bubble. Postcard: a polaroid.
		const photoMode = stippl ? (config.photos ?? "bubbles") : "polaroid";
		const bubble = px(196);
		// The map's card, scaled down: 368 x 416 with a 34 radius and a 14 white border in
		// photoCards.ts, so 150 wide keeps the same proportions -- radius 14, border 6.
		const fanW = px(150);
		const fanH = (fanW * 416) / 368;
		const cardW = photoMode === "bubbles" ? bubble + px(16) : photoMode === "cards" ? px(262) : px(232);
		const cardPhoto = px(204);
		const cardH = photoMode === "bubbles" ? bubble + px(16) + px(40) : photoMode === "cards" ? fanH + px(64) : px(14 + 204 + 66);
		const withPhoto = stopsXY.map((_, i) => ({ i, photo: (reelData?.[i]?.photos || []).find((m: any) => !isVideoMedia(m)) })).filter((c) => c.photo);
		const picked = spread(withPhoto.length, MAX_CARDS).map((k) => withPhoto[k]);
		const ringXs = ringsXY.flat().map((p) => p[0]);
		const centerX = ringXs.length ? (Math.min(...ringXs) + Math.max(...ringXs)) / 2 : centre[0];
		const slots = layoutCards(
			picked.map((c) => stopsXY[c.i]),
			{
				centerX,
				width,
				cardW,
				cardH,
				top: height * layout.cards.top,
				bottom: height * layout.cards.bottom,
				left: width * layout.cards.left,
				right: width * layout.cards.right,
				margin: px(30),
			}
		);

		const placed = picked.map((c, k) => {
			const slot = slots[k];
			const at = reach(stopAt[c.i] ?? 0);
			const p = spring({ frame: frame - at, fps, config: { damping: 12, stiffness: 120 } });
			// Leaving: a polaroid is blown off the film; a bubble goes back into its dot, the way it came.
			const out = interpolate(frame, [CARDS_OUT + k * 3, CARDS_OUT + k * 3 + (stippl ? 14 : 22)], [0, 1], {
				...clamp,
				easing: stippl ? Easing.in(Easing.back(1.6)) : Easing.in(Easing.cubic),
			});
			return { c, k, slot, p, out, from: stopsXY[c.i], name: currentTrip?.destinations?.[c.i]?.cityEnglish || reelData?.[c.i]?.city || "" };
		});

		const bubbles = placed.map(({ c, k, slot, p, out, from, name }) => {
			const q = p * (1 - out);
			if (q <= 0.001) return null;
			const x = from[0] + (slot.x - from[0]) * q;
			const y = from[1] + (slot.y - from[1]) * q;
			const label = spring({ frame: frame - reach(stopAt[c.i] ?? 0) - 7, fps, config: { damping: 12, stiffness: 160 } }) * (1 - out);
			const bob = Math.sin((frame + k * 17) / 16) * px(4);
			return (
				<div
					key={c.i}
					style={{
						position: "absolute",
						left: x - cardW / 2,
						top: y - cardH / 2 + bob,
						width: cardW,
						height: cardH,
						transform: `scale(${(0.06 + 0.94 * q) * slot.scale})`,
						transformOrigin: `50% ${((bubble / 2 + px(8)) / cardH) * 100}%`,
					}}
				>
					<div
						style={{
							width: bubble,
							height: bubble,
							borderRadius: "50%",
							border: `${px(8)}px solid #fff`,
							overflow: "hidden",
							background: "#e6eef0",
							boxShadow: tealShadow(px(10), px(26), 0.35),
						}}
					>
						<Img src={mediaUrl(c.photo)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
					</div>
					<div style={{ position: "absolute", left: 0, right: 0, top: bubble - px(4), display: "flex", justifyContent: "center" }}>
						<div
							style={{
								height: px(54),
								lineHeight: `${px(54)}px`,
								padding: `0 ${px(22)}px`,
								borderRadius: px(27),
								background: "#fff",
								color: TEAL,
								fontFamily: "Poppins",
								fontWeight: 600,
								fontSize: px(28),
								whiteSpace: "nowrap",
								boxShadow: tealShadow(px(4), px(16)),
								transform: `translateY(${px(14) * (1 - label)}px) scale(${0.6 + 0.4 * label})`,
								opacity: Math.min(1, label * 2),
							}}
						>
							{name}
						</div>
					</div>
				</div>
			);
		});

		// The fan: up to three of the stop's photographs as the map shows them at a stop in cards mode --
		// rounded, white-bordered, each on its own spring a few frames after the last, spread and tilted
		// by its place in the fan (13 degrees a card), and the fan turning the other way on alternate
		// stops. The whole fan comes out of the dot and goes back into it, like the bubbles.
		const fans = placed.map(({ c, k, slot, p, out, from, name }) => {
			const q = p * (1 - out);
			if (q <= 0.001) return null;
			const x = from[0] + (slot.x - from[0]) * q;
			const y = from[1] + (slot.y - from[1]) * q;
			const shots = ((reelData?.[c.i]?.photos || []) as any[]).filter((m) => !isVideoMedia(m)).slice(0, 3);
			const centreCard = (shots.length - 1) / 2;
			const turn = k % 2 === 0 ? 1 : -1;
			const at = reach(stopAt[c.i] ?? 0);
			const label = spring({ frame: frame - at - 8, fps, config: { damping: 12, stiffness: 160 } }) * (1 - out);
			return (
				<div key={c.i} style={{ position: "absolute", left: x, top: y, width: 0, height: 0, transform: `scale(${(0.06 + 0.94 * q) * slot.scale})` }}>
					{shots.map((shot, j) => {
						const cs = spring({ frame: frame - at - j * 5, fps, config: { mass: 1, stiffness: 260, damping: 16 } });
						const off = j - centreCard;
						const tilt = interpolate(cs, [0, 1], [-24 * turn, off * 13 * turn]);
						const spread = off * fanW * 0.28 * turn * cs;
						const lift = -(centreCard - Math.abs(off)) * px(8) - px(26);
						return (
							<div
								key={j}
								style={{
									position: "absolute",
									left: -fanW / 2,
									top: -fanH / 2,
									width: fanW,
									height: fanH,
									borderRadius: px(14),
									border: `${px(6)}px solid #fff`,
									boxSizing: "border-box",
									overflow: "hidden",
									background: "#e6eef0",
									boxShadow: tealShadow(px(8), px(22), 0.35),
									transform: `translate(${spread}px, ${lift}px) rotate(${tilt}deg) scale(${0.2 + 0.8 * cs})`,
									opacity: Math.min(1, cs * 3),
								}}
							>
								<Img src={mediaUrl(shot)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
							</div>
						);
					})}
					<div style={{ position: "absolute", left: -px(200), width: px(400), top: fanH / 2 - px(10), display: "flex", justifyContent: "center" }}>
						<div
							style={{
								height: px(54),
								lineHeight: `${px(54)}px`,
								padding: `0 ${px(22)}px`,
								borderRadius: px(27),
								background: "#fff",
								color: TEAL,
								fontFamily: "Poppins",
								fontWeight: 600,
								fontSize: px(28),
								whiteSpace: "nowrap",
								boxShadow: tealShadow(px(4), px(16)),
								transform: `translateY(${px(14) * (1 - label)}px) scale(${0.6 + 0.4 * label})`,
								opacity: Math.min(1, label * 2),
							}}
						>
							{name}
						</div>
					</div>
				</div>
			);
		});

		// The dotted line from each stop to its bubble. Under the bubble, so the bubble sits on its end.
		const tethers = stippl
			? placed.map(({ c, slot, p, out, from }) => {
					const q = interpolate(p, [0.6, 1], [0, 1], clamp) * (1 - out);
					if (q <= 0.01) return null;
					return (
						<line
							key={c.i}
							x1={from[0]}
							y1={from[1]}
							x2={slot.x}
							y2={slot.y}
							stroke="#fff"
							strokeOpacity={0.85 * q}
							strokeWidth={px(6)}
							strokeLinecap="round"
							strokeDasharray={`0 ${px(14)}`}
						/>
					);
				})
			: null;

		const cards = placed.map(({ c, k, slot, p, out, from, name }) => {
			if (p <= 0.001) return null;
			const tilt = CARD_TILT[k % CARD_TILT.length];
			const wobble = Math.sin((frame + k * 13) / 14) * 1.2;
			const dir = slot.side === "left" ? -1 : 1;
			const x = from[0] + (slot.x - from[0]) * p + dir * px(900) * out;
			const y = from[1] + (slot.y - from[1]) * p - px(220) * out;
			return (
				<div
					key={c.i}
					style={{
						position: "absolute",
						left: x - cardW / 2,
						top: y - cardH / 2,
						width: cardW,
						height: cardH,
						padding: px(14),
						boxSizing: "border-box",
						background: "#fdfcf8",
						borderRadius: px(4),
						boxShadow: `0 ${px(10)}px ${px(28)}px rgba(0,0,0,.35)`,
						transform: `rotate(${tilt * (1 + 3 * (1 - p)) + wobble + dir * 35 * out}deg) scale(${(0.2 + 0.8 * p) * slot.scale})`,
					}}
				>
					<div style={{ width: cardPhoto, height: cardPhoto, overflow: "hidden", background: "#ddd" }}>
						<Img src={mediaUrl(c.photo)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
					</div>
					<div
						style={{
							fontFamily: handwriting,
							fontWeight: 700,
							fontSize: px(46),
							lineHeight: `${px(62)}px`,
							color: "#2a2a2a",
							textAlign: "center",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{name}
					</div>
					{/* A strip of tape, because a card needs something holding it to the film. */}
					<div
						style={{
							position: "absolute",
							left: "50%",
							top: px(-16),
							width: px(96),
							height: px(30),
							marginLeft: px(-48),
							background: "rgba(250,238,200,.78)",
							transform: `rotate(${-tilt * 0.6}deg)`,
						}}
					/>
				</div>
			);
		});

		// The route: a dotted line of round dots in the house style -- stippellijn, where the name comes
		// from -- revealed by a solid stroke in a mask, since a dash pattern cannot also be the reveal.
		// Postcard keeps the solid pen line.

		return (
			<AbsoluteFill>
				<svg width={0} height={0} style={{ position: "absolute" }}>
					<defs>
						<mask id="route-cut-film" maskUnits="userSpaceOnUse" x={0} y={0} width={width} height={height}>
							<rect x={0} y={0} width={width} height={height} fill="#fff" />
							<path
								d={hole}
								fill={hasCountry ? "#000" : "none"}
								fillOpacity={windowOpen}
								stroke="#000"
								strokeWidth={cut}
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</mask>
						{/* The rim sits just OUTSIDE the hole: a white edge on the film, not on the map. */}
						<mask id="route-cut-rim" maskUnits="userSpaceOnUse" x={0} y={0} width={width} height={height}>
							<rect x={0} y={0} width={width} height={height} fill="#fff" />
							<path d={hole} fill={hasCountry ? "#000" : "none"} stroke="#000" strokeWidth={cut} strokeLinecap="round" strokeLinejoin="round" />
						</mask>
						<mask id="route-cut-reveal" maskUnits="userSpaceOnUse" x={0} y={0} width={width} height={height}>
							<path
								d={d}
								fill="none"
								stroke="#fff"
								strokeWidth={px(48)}
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeDasharray={L}
								strokeDashoffset={L * (1 - draw)}
							/>
						</mask>
					</defs>
				</svg>

				{frame < CUT_OUT ? (
					// The shadow is on a wrapper, not on the masked layer: a filter runs before the mask, so on
					// the same element the mask would cut the shadow away with the film.
					<AbsoluteFill
						style={{
							filter: `drop-shadow(0 ${px(12)}px ${px(36)}px rgba(${stippl ? "15,62,74" : "0,0,0"},${0.6 * Math.max(windowOpen, cut > 0 ? 1 : 0)}))`,
						}}
					>
						<AbsoluteFill style={{ mask: "url(#route-cut-film)", WebkitMask: "url(#route-cut-film)" } as any}>
							<AbsoluteFill style={{ transform: `scale(${push})` }}>{clip}</AbsoluteFill>
							{scrim}
						</AbsoluteFill>
					</AbsoluteFill>
				) : null}

				<svg width={width} height={height} style={{ position: "absolute", inset: 0 }}>
					{hasCountry && frame < CUT_OUT ? (
						<>
							{/* A wash so the shape reads on the film before it becomes a window: brand green in the
							    house style, white on the postcard. */}
							<path d={countryD} fill={stippl ? brandColor : "#fff"} fillOpacity={(stippl ? 0.22 : 0.14) * outline * (1 - windowOpen)} />
							<g mask="url(#route-cut-rim)">
								<path
									d={countryD}
									fill="none"
									stroke="#fff"
									strokeWidth={cut + rim}
									strokeLinejoin="round"
									strokeDasharray={cut > 0 ? undefined : Lc}
									strokeDashoffset={cut > 0 ? undefined : Lc * (1 - outline)}
								/>
							</g>
						</>
					) : null}
					{hasCountry && frame >= CUT_OUT ? (
						<path d={countryD} fill="none" stroke="#fff" strokeOpacity={0.75 * ghost} strokeWidth={px(3)} strokeLinejoin="round" />
					) : null}
					{tethers}
				</svg>

				{photoMode === "cards" ? fans : photoMode === "bubbles" ? bubbles : cards}

				<svg width={width} height={height} style={{ position: "absolute", inset: 0 }}>
					<g opacity={ghost}>
						{stippl ? (
							<g mask="url(#route-cut-reveal)">
								<path
									d={d}
									fill="none"
									stroke="rgba(15,62,74,.3)"
									strokeWidth={px(17)}
									strokeLinecap="round"
									strokeDasharray={`0 ${dotGap}`}
									transform={`translate(0 ${px(2)})`}
								/>
								<path d={d} fill="none" stroke="#fff" strokeWidth={px(12)} strokeLinecap="round" strokeDasharray={`0 ${dotGap}`} />
							</g>
						) : (
							<>
								<path
									d={d}
									fill="none"
									stroke="rgba(0,0,0,.3)"
									strokeWidth={px(16)}
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeDasharray={L}
									strokeDashoffset={L * (1 - draw)}
								/>
								<path
									d={d}
									fill="none"
									stroke="#fff"
									strokeWidth={px(9)}
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeDasharray={L}
									strokeDashoffset={L * (1 - draw)}
								/>
							</>
						)}
						{stopAt.slice(1).map((vi, i) => {
							const [x, y] = xy[vi] || [0, 0];
							return dot(i, x, y, spring({ frame: frame - reach(vi), fps, config: { damping: 9, stiffness: 220 } }));
						})}
					</g>
					{pulse(CUT_IN)}
					{pulse(CUT_IN + 22)}
					{pulse(introFrames - 12)}
					{dot("first", fx, fy, spring({ frame: frame - DRAW_IN + 4, fps, config: { damping: 9, stiffness: 220 } }) * (0.7 + 0.3 * handOver), handOver)}
					{ride > 0.01 ? (
						<g transform={`translate(${head.at[0]} ${head.at[1]}) scale(${ride})`}>
							{stippl ? (
								<IconTile size={px(80)} glyph={glyph} color={brandColor} facing={facing} />
							) : (
								<>
									<circle r={px(38)} fill={brandColor} stroke="#fff" strokeWidth={px(5)} />
									<g transform={`scale(${facing} 1)`}>
										<Glyph name={glyph} size={px(50)} color="#fff" />
									</g>
								</>
							)}
						</g>
					) : null}
				</svg>

				{words}
			</AbsoluteFill>
		);
	}

	// ---- globe-bubble ------------------------------------------------------------------------------
	if (style === "globe-bubble") {
		const cx = stageX;
		const cy = stageY;
		const pop = spring({ frame: frame - G_POP, fps, config: { damping: 11, stiffness: 150 } });
		const expand = interpolate(frame, G_EXPAND, [0, 1], { ...clamp, easing: Easing.in(Easing.cubic) });
		const r = bubbleR * pop + (Math.hypot(width, height) / 2 + px(40) - bubbleR) * expand;
		const ghost = interpolate(frame, G_GHOST, [1, 0], clamp);
		const push = interpolate(frame, [0, G_EXPAND[1]], [1.02, 1.2], { ...clamp, easing: Easing.in(Easing.quad) });

		// The stops as moons around the window, each at the compass direction its stop lies from the
		// middle of the trip -- geographic rather than projected, so a moon does not drift while the
		// globe is still closing in. Neighbours are pushed apart until 38 degrees separate them.
		const lngs = flat.map((c) => c[0]);
		const lats = flat.map((c) => c[1]);
		const midLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
		const midLat = (Math.min(...lats) + Math.max(...lats)) / 2;
		const withPhoto = stopsGeo.map((g, i) => ({ i, g, photo: (reelData?.[i]?.photos || []).find((m: any) => !isVideoMedia(m)) })).filter((c) => c.photo);
		const moons = spread(withPhoto.length, MAX_CARDS).map((k) => {
			const c = withPhoto[k];
			const ang = Math.atan2(-(c.g[1] - midLat), (c.g[0] - midLng) * Math.cos((midLat * Math.PI) / 180));
			return { ...c, ang: isFinite(ang) ? ang : 0 };
		});
		const order = moons.map((m, k) => k).sort((a, b) => moons[a].ang - moons[b].ang);
		const GAP = (38 * Math.PI) / 180;
		for (let pass = 0; pass < 12; pass++) {
			for (let j = 0; j < order.length; j++) {
				const a = moons[order[j]];
				const b = moons[order[(j + 1) % order.length]];
				if (order.length < 2) break;
				let delta = b.ang - a.ang;
				if (j === order.length - 1) delta += 2 * Math.PI;
				if (delta < GAP) {
					a.ang -= (GAP - delta) / 2;
					b.ang += (GAP - delta) / 2;
				}
			}
		}
		const moonD = px(118);
		const orbit = bubbleR + px(100);
		const placedMoons = moons.map((m, k) => {
			const p = spring({ frame: frame - reach(stopAt[m.i] ?? 0), fps, config: { damping: 12, stiffness: 130 } });
			const out = interpolate(frame, [G_MOONS_OUT + k * 2, G_MOONS_OUT + k * 2 + 14], [0, 1], { ...clamp, easing: Easing.in(Easing.cubic) });
			const dist = orbit + px(520) * out;
			const to = [cx + Math.cos(m.ang) * dist, cy + Math.sin(m.ang) * dist];
			const from = stopsXY[m.i] || [cx, cy];
			return { ...m, k, p, out, at: [from[0] + (to[0] - from[0]) * p, from[1] + (to[1] - from[1]) * p], from };
		});

		return (
			<AbsoluteFill>
				<svg width={0} height={0} style={{ position: "absolute" }}>
					<defs>
						<mask id="globe-film" maskUnits="userSpaceOnUse" x={0} y={0} width={width} height={height}>
							<rect x={0} y={0} width={width} height={height} fill="#fff" />
							<circle cx={cx} cy={cy} r={Math.max(0, r)} fill="#000" />
						</mask>
						<clipPath id="globe-clip">
							<circle cx={cx} cy={cy} r={Math.max(0, r)} />
						</clipPath>
					</defs>
				</svg>

				{expand < 1 ? (
					<AbsoluteFill style={{ filter: `drop-shadow(0 ${px(10)}px ${px(30)}px rgba(15,62,74,.55))` }}>
						<AbsoluteFill style={{ mask: "url(#globe-film)", WebkitMask: "url(#globe-film)" } as any}>
							<AbsoluteFill style={{ transform: `scale(${push})` }}>{clip}</AbsoluteFill>
							{scrim}
						</AbsoluteFill>
					</AbsoluteFill>
				) : null}

				<svg width={width} height={height} style={{ position: "absolute", inset: 0 }}>
					{expand < 1 && pop > 0.01 ? <circle cx={cx} cy={cy} r={r + px(5)} fill="none" stroke="#fff" strokeWidth={px(10)} opacity={1 - expand} /> : null}
					{placedMoons.map((m) => {
						// The tether lets go first: stretched after a moon on its way off screen it read as a
						// string, not a route.
						const q = interpolate(m.p, [0.5, 1], [0, 1], clamp) * (1 - Math.min(1, m.out * 4));
						return q > 0.01 ? (
							<line
								key={m.i}
								x1={m.from[0]}
								y1={m.from[1]}
								x2={m.at[0]}
								y2={m.at[1]}
								stroke="#fff"
								strokeOpacity={0.85 * q}
								strokeWidth={px(6)}
								strokeLinecap="round"
								strokeDasharray={`0 ${px(14)}`}
							/>
						) : null;
					})}
					<g clipPath="url(#globe-clip)">
						<g opacity={ghost}>
							{dottedRoute("globe-reveal")}
							{laterStops()}
						</g>
						{pulse(G_DIVE)}
						{pulse(G_DIVE + 20)}
						{pulse(introFrames - 12)}
						{firstStop()}
						{vehicleTile()}
					</g>
				</svg>

				{placedMoons.map((m) => {
					const s = m.p * (1 - m.out);
					if (s <= 0.01) return null;
					const label = spring({ frame: frame - reach(stopAt[m.i] ?? 0) - 7, fps, config: { damping: 12, stiffness: 160 } }) * (1 - m.out);
					const name = currentTrip?.destinations?.[m.i]?.cityEnglish || reelData?.[m.i]?.city || "";
					return (
						<div key={m.i} style={{ position: "absolute", left: m.at[0], top: m.at[1], width: 0, height: 0, transform: `scale(${0.1 + 0.9 * s})` }}>
							<div
								style={{
									position: "absolute",
									left: -moonD / 2,
									top: -moonD / 2,
									width: moonD,
									height: moonD,
									borderRadius: "50%",
									border: `${px(6)}px solid #fff`,
									boxSizing: "border-box",
									overflow: "hidden",
									background: "#e6eef0",
									boxShadow: tealShadow(px(8), px(22), 0.35),
								}}
							>
								<Img src={mediaUrl(m.photo)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
							</div>
							<div style={{ position: "absolute", left: -px(160), width: px(320), top: moonD / 2 - px(8), display: "flex", justifyContent: "center" }}>
								<div
									style={{
										height: px(44),
										lineHeight: `${px(44)}px`,
										padding: `0 ${px(18)}px`,
										borderRadius: px(22),
										background: "#fff",
										color: TEAL,
										fontFamily: "Poppins",
										fontWeight: 600,
										fontSize: px(23),
										whiteSpace: "nowrap",
										boxShadow: tealShadow(px(3), px(12)),
										transform: `scale(${0.6 + 0.4 * label})`,
										opacity: Math.min(1, label * 2),
									}}
								>
									{name}
								</div>
							</div>
						</div>
					);
				})}

				{words}
			</AbsoluteFill>
		);
	}

	// ---- card-flip ---------------------------------------------------------------------------------
	if (style === "card-flip") {
		const shrink = interpolate(frame, F_SHRINK, [0, 1], { ...clamp, easing: Easing.inOut(Easing.cubic) });
		const grow = interpolate(frame, F_GROW, [0, 1], { ...clamp, easing: Easing.inOut(Easing.cubic) });
		const flip = interpolate(frame, F_FLIP, [0, 1], { ...clamp, easing: Easing.inOut(Easing.cubic) });
		const theta = 180 * flip;
		// How much of a card it is: 0 is the whole frame, 1 is the card. The map's card proportions --
		// 368 x 416, a 34 radius and a 14 border in photoCards.ts -- at 700 wide.
		const k = shrink * (1 - grow);
		const w = width + (flipW - width) * k;
		const h = height + (flipH - height) * k;
		const ccx = width / 2 + (flipCx - width / 2) * k;
		const ccy = height / 2 + (flipCy - height / 2) * k;
		const radius = (34 / 368) * flipW * k;
		const border = (14 / 368) * flipW * k;
		const phi = (-6 + Math.sin(frame / 15) * 1.4) * k;
		const D = px(2600);
		const backVisible = theta > 90;
		const ghost = interpolate(frame, F_GHOST, [1, 0], clamp);

		// The card's outline as the browser draws it under `perspective(D) rotateY(theta) rotate(phi)`,
		// worked out here too, because the back of the card is not an element: it is a hole in the
		// backdrop the map shows through, and a hole needs its shape.
		const outline: number[][] = [];
		const hw = w / 2;
		const hh = h / 2;
		const rr = Math.min(radius, hw, hh);
		[
			[hw - rr, -hh + rr, -90],
			[hw - rr, hh - rr, 0],
			[-hw + rr, hh - rr, 90],
			[-hw + rr, -hh + rr, 180],
		].forEach(([ox, oy, a0]) => {
			for (let i = 0; i <= 6; i++) {
				const a = ((a0 + (90 * i) / 6) * Math.PI) / 180;
				outline.push([ox + Math.cos(a) * rr, oy + Math.sin(a) * rr]);
			}
		});
		const sT = Math.sin((theta * Math.PI) / 180);
		const cT = Math.cos((theta * Math.PI) / 180);
		const sP = Math.sin((phi * Math.PI) / 180);
		const cP = Math.cos((phi * Math.PI) / 180);
		const onScreen = outline.map(([x, y]) => {
			const xr = x * cP - y * sP;
			const yr = x * sP + y * cP;
			const sc = D / (D + xr * sT);
			return [ccx + xr * cT * sc, ccy + yr * sc];
		});
		const polyD = pathOf(onScreen, true);
		const shade = 0.45 * sT;
		const ringsXY = rings.map((ring) => smoothed(place(ring), 4));
		const countryD = ringsXY.map((ring) => pathOf(ring, true)).join(" ");

		return (
			<AbsoluteFill>
				<svg width={0} height={0} style={{ position: "absolute" }}>
					<defs>
						<mask id="flip-backdrop" maskUnits="userSpaceOnUse" x={0} y={0} width={width} height={height}>
							<rect x={0} y={0} width={width} height={height} fill="#fff" />
							{backVisible ? <path d={polyD} fill="#000" /> : null}
						</mask>
						<clipPath id="flip-clip">{backVisible ? <path d={polyD} /> : <rect x={0} y={0} width={0} height={0} />}</clipPath>
						<filter id="flip-blur" x="-20%" y="-20%" width="140%" height="140%">
							<feGaussianBlur stdDeviation={px(26)} />
						</filter>
					</defs>
				</svg>

				{grow < 1 ? (
					// Behind the card: deep teal with the dot grid, stippl's own ground.
					<AbsoluteFill
						style={
							{
								mask: "url(#flip-backdrop)",
								WebkitMask: "url(#flip-backdrop)",
								background: `radial-gradient(circle, rgba(255,255,255,.09) ${px(3)}px, transparent ${px(3.5)}px) 0 0 / ${px(34)}px ${px(34)}px, linear-gradient(160deg, #184C59, ${TEAL})`,
								opacity: Math.min(1, shrink * 2),
							} as any
						}
					/>
				) : null}

				{k > 0.01 ? (
					<svg width={width} height={height} style={{ position: "absolute", inset: 0 }}>
						<path d={polyD} fill="rgba(6,28,34,.55)" transform={`translate(0 ${px(28)})`} filter="url(#flip-blur)" opacity={k} />
					</svg>
				) : null}

				{!backVisible ? (
					<div
						style={{
							position: "absolute",
							left: ccx - w / 2,
							top: ccy - h / 2,
							width: w,
							height: h,
							borderRadius: radius,
							border: `${border}px solid #fff`,
							boxSizing: "border-box",
							overflow: "hidden",
							background: "#000",
							transform: `perspective(${D}px) rotateY(${theta}deg) rotate(${phi}deg)`,
						}}
					>
						<AbsoluteFill>{clip}</AbsoluteFill>
						<AbsoluteFill style={{ opacity: 1 - k }}>{scrim}</AbsoluteFill>
						<AbsoluteFill style={{ background: `rgba(15,62,74,${shade})` }} />
					</div>
				) : null}

				<svg width={width} height={height} style={{ position: "absolute", inset: 0 }}>
					{backVisible ? (
						<>
							<path d={polyD} fill={`rgba(15,62,74,${shade})`} />
							<g clipPath="url(#flip-clip)">
								{countryD ? <path d={countryD} fill="none" stroke="#fff" strokeOpacity={0.8 * ghost} strokeWidth={px(3)} strokeLinejoin="round" /> : null}
								<g opacity={ghost}>
									{dottedRoute("flip-reveal")}
									{laterStops()}
								</g>
								{pulse(F_DIVE)}
								{pulse(F_DIVE + 22)}
								{pulse(introFrames - 12)}
								{firstStop()}
								{vehicleTile()}
							</g>
							{border > 0.5 ? <path d={polyD} fill="none" stroke="#fff" strokeWidth={border * 2} strokeLinejoin="round" /> : null}
						</>
					) : null}
				</svg>

				{words}
			</AbsoluteFill>
		);
	}

	// ---- the circle styles -------------------------------------------------------------------------
	// The trend: a map, a circle drawn round a place by hand, and a zoom into it. The circle is never
	// round -- see `scribbleLoop` -- and it is in the brand's pink rather than a generic red.
	const PINK = "#e84d7e";
	/** The circle, its arrow and its note: the brand pink unless `intro.circleColor` says otherwise. */
	const circleInk = config.circleColor ?? PINK;
	const circleName = String(config.circleLabel ?? currentTrip?.destinations?.[0]?.cityEnglish ?? reelData?.[0]?.city ?? "");
	const circleSeed = hashSeed(circleName || "stippl");
	/** The circle, drawn up to `progress`, scaled by `grow` round its own centre. */
	const circleMark = (cx: number, cy: number, rx: number, ry: number, progress: number, grow = 1, opacity = 1, color = circleInk) => {
		if (progress <= 0.001 || opacity <= 0.001) return null;
		const loop = scribbleLoop(cx, cy, rx * grow, ry * grow, -9, circleSeed);
		const shown = loop.slice(0, Math.max(2, Math.round(progress * (loop.length - 1)) + 1));
		const d = markerStroke(shown, px(15) * Math.min(1.6, Math.sqrt(grow)));
		return (
			<g opacity={opacity}>
				<path d={d} fill="rgba(15,62,74,.28)" transform={`translate(0 ${px(3)})`} />
				<path d={d} fill={color} />
			</g>
		);
	};
	/** The handwritten name up and to the right of the circle, with a little arrow pointing into it. */
	const circleNote = (cx: number, cy: number, rx: number, ry: number, progress: number, opacity = 1) => {
		if (progress <= 0.001 || opacity <= 0.001) return null;
		const tip = [cx + rx * 0.62, cy - ry * 0.86];
		const tail = [cx + rx * 1.05, cy - ry * 1.55];
		const ctrl = [cx + rx * 1.18, cy - ry * 1.02];
		const arrow = interpolate(progress, [0, 0.6], [0, 1], clamp);
		const words = interpolate(progress, [0.4, 1], [0, 1], clamp);
		const n = 24;
		const curve = Array.from({ length: n + 1 }, (_, i) => {
			const t = (i / n) * arrow;
			const u = 1 - t;
			return [u * u * tail[0] + 2 * u * t * ctrl[0] + t * t * tip[0], u * u * tail[1] + 2 * u * t * ctrl[1] + t * t * tip[1]];
		});
		const end = curve[curve.length - 1];
		const prev = curve[Math.max(0, curve.length - 3)];
		const ang = Math.atan2(end[1] - prev[1], end[0] - prev[0]);
		const head = (da: number) => [end[0] - Math.cos(ang + da) * px(22), end[1] - Math.sin(ang + da) * px(22)];
		return (
			<g opacity={opacity}>
				{arrow > 0.02 ? <path d={markerStroke(curve, px(9))} fill={circleInk} /> : null}
				{arrow > 0.95 ? (
					<path
						d={`M${head(0.55).join(" ")} L${end.join(" ")} L${head(-0.55).join(" ")}`}
						fill="none"
						stroke={circleInk}
						strokeWidth={px(7)}
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				) : null}
				<text
					x={tail[0] - px(10)}
					y={tail[1] - px(16)}
					fontFamily={handwriting}
					fontWeight={700}
					fontSize={px(68)}
					fill={circleInk}
					stroke="#fff"
					strokeWidth={px(10)}
					paintOrder="stroke"
					strokeLinejoin="round"
					textAnchor="middle"
					opacity={words}
					transform={`rotate(-6 ${tail[0]} ${tail[1]})`}
				>
					{circleName}
				</text>
			</g>
		);
	};
	// The title needs a ground on the map as much as on the film.
	const mapScrim = (opacity: number) => (
		<AbsoluteFill
			style={{
				opacity,
				background: "linear-gradient(to bottom, rgba(15,62,74,.62) 0%, rgba(15,62,74,.22) 32%, rgba(15,62,74,0) 50%)",
			}}
		/>
	);

	if (style === "circle-dive") {
		// The hook is the note: above and to the left of the circle, with an arrow down into it, as the
		// "we went here for the beach" posts do. No title block: the hook is the only words on screen.
		const [cx, cy] = project(stop);
		const rx = cdRx;
		const ry = cdRx * 0.74;
		const caps = config.noteFont === "caps";
		const color = config.circleColor ?? (caps ? "#ffffff" : PINK);
		const draw = interpolate(frame, CD_CIRCLE, [0, 1], { ...clamp, easing: Easing.inOut(Easing.sin) });
		const note = interpolate(frame, CD_NOTE, [0, 1], { ...clamp, easing: Easing.out(Easing.cubic) });
		// In the dive the circle rides the zoom more slowly than the ground, opening round the stop, and
		// is gone before the camera tilts far enough to make it an oval on the floor.
		const cam = introCameraAt(Math.min(frame, introFrames - 1), camera, numbers);
		const alt = cam && cam.kind === "free" ? cam.altitude : cdAltitude;
		const dive = interpolate(frame, [CD_DIVE, CD_ORBIT], [0, 1], clamp);
		const grow = frame < CD_DIVE ? 1 : Math.pow((cdAltitude * 0.86) / alt, 0.42);
		const circleOpacity = 1 - interpolate(dive, [0.12, 0.42], [0, 1], clamp);
		const noteOpacity = 1 - interpolate(dive, [0, 0.14], [0, 1], clamp);

		// The same fallback as the other circle intros, down to the first stop's city in `reelData`: a
		// reel without `currentTrip` would otherwise draw an arrow pointing at nothing.
		const text = circleName;
		const perLine = caps ? 15 : 17;
		const lines = text.split(/\s+/).reduce((acc: string[], w) => {
			const last = acc[acc.length - 1];
			if (last && (last + " " + w).length <= perLine) acc[acc.length - 1] = last + " " + w;
			else acc.push(w);
			return acc;
		}, []);
		const size = caps ? px(58) : px(80);
		const lineH = size * (caps ? 1.12 : 0.98);
		const tip = [cx - rx * 1.02, cy - ry * 0.46];
		const tail = [cx - rx * 1.22, cy - ry * 1.62];
		const ctrl = [cx - rx * 1.52, cy - ry * 0.86];
		const arrow = interpolate(note, [0, 0.55], [0, 1], clamp);
		const words = interpolate(note, [0.35, 1], [0, 1], clamp);
		const n = 24;
		const curve = Array.from({ length: n + 1 }, (_, i) => {
			const t = (i / n) * arrow;
			const u = 1 - t;
			return [u * u * tail[0] + 2 * u * t * ctrl[0] + t * t * tip[0], u * u * tail[1] + 2 * u * t * ctrl[1] + t * t * tip[1]];
		});
		const end = curve[curve.length - 1];
		const prev = curve[Math.max(0, curve.length - 3)];
		const ang = Math.atan2(end[1] - prev[1], end[0] - prev[0]);
		const head = (da: number) => [end[0] - Math.cos(ang + da) * px(24), end[1] - Math.sin(ang + da) * px(24)];
		const textX = Math.min(width - px(300), Math.max(px(300), tail[0] + px(40)));
		const lastY = tail[1] - px(34);
		const noteColor = caps ? "#ffffff" : color;
		// The circle in the chosen hand. Everything is drawn round (cx, cy) at rx x ry, scaled by `grow`.
		const circleStyle = config.circleStyle ?? "scribble";
		const R = (k = 1) => [rx * grow * k, ry * grow * k];
		// A clean ring, for the styles that are not a hand's loop: the dotted treasure-map ring and the
		// spotlight's hole. Same tilt as the scribble, starting at the same place, a whole turn.
		const ring = (k = 1, samples = 120) => {
			const [a, b] = R(k);
			const tilt = (-9 * Math.PI) / 180;
			return Array.from({ length: samples + 1 }, (_, i) => {
				const t = -Math.PI * 0.5 + (i / samples) * Math.PI * 2;
				const x = Math.cos(t) * a;
				const y = Math.sin(t) * b;
				return [cx + x * Math.cos(tilt) - y * Math.sin(tilt), cy + x * Math.sin(tilt) + y * Math.cos(tilt)];
			});
		};
		const pathD = (pts: number[][]) => pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
		const partial = (pts: number[][], t: number) => pts.slice(0, Math.max(2, Math.round(t * (pts.length - 1)) + 1));
		const circle = (() => {
			if (circleOpacity <= 0.001 || draw <= 0.001) return null;
			if (circleStyle === "double") {
				const [a1, b1] = R(1);
				const [a2, b2] = R(1.07);
				// Two loops of the same hand, the second a little wider and turned: a different seed is a
				// different wobble, which is what makes it read as gone round twice rather than traced.
				const first = scribbleLoop(cx, cy, a1, b1, -9, circleSeed);
				const second = scribbleLoop(cx, cy, a2, b2, -3, circleSeed + 3);
				const w = px(12) * Math.min(1.6, Math.sqrt(grow));
				const t1 = interpolate(draw, [0, 0.55], [0, 1], clamp);
				const t2 = interpolate(draw, [0.45, 1], [0, 1], clamp);
				return (
					<g opacity={circleOpacity}>
						<path d={markerStroke(partial(first, t1), w)} fill="rgba(15,62,74,.28)" transform={`translate(0 ${px(3)})`} />
						<path d={markerStroke(partial(first, t1), w)} fill={color} />
						{t2 > 0.01 ? <path d={markerStroke(partial(second, t2), w * 0.8)} fill={color} /> : null}
					</g>
				);
			}
			if (circleStyle === "dashed") {
				// A clean dotted ring drawn on, then an X on the very spot.
				const drawn = partial(ring(1), draw);
				const x = interpolate(draw, [0.75, 1], [0, 1], clamp);
				const arm = px(34) * Math.min(1.5, Math.sqrt(grow));
				const stroke1 = [
					[cx - arm, cy - arm],
					[cx - arm + 2 * arm * Math.min(1, x * 2), cy - arm + 2 * arm * Math.min(1, x * 2)],
				];
				const stroke2 = [
					[cx + arm, cy - arm],
					[cx + arm - 2 * arm * Math.max(0, x * 2 - 1), cy - arm + 2 * arm * Math.max(0, x * 2 - 1)],
				];
				return (
					<g opacity={circleOpacity}>
						<path d={pathD(drawn)} fill="none" stroke={color} strokeWidth={px(9)} strokeLinecap="round" strokeDasharray={`${px(2)} ${px(24)}`} />
						{x > 0.01 ? <path d={markerStroke(stroke1, px(13))} fill={color} /> : null}
						{x > 0.5 ? <path d={markerStroke(stroke2, px(13))} fill={color} /> : null}
					</g>
				);
			}
			if (circleStyle === "spotlight") {
				// The world outside the circle dims as the ring is drawn; the dive opens the light up.
				const dim = 0.62 * interpolate(draw, [0, 1], [0, 1], clamp) * circleOpacity;
				return (
					<g>
						<defs>
							<mask id="cd-spot">
								<rect x={0} y={0} width={width} height={height} fill="#fff" />
								<path d={pathD(ring(1.02)) + " Z"} fill="#000" style={{ filter: `blur(${px(10)}px)` }} />
							</mask>
						</defs>
						<rect x={0} y={0} width={width} height={height} fill="#06131a" opacity={dim} mask="url(#cd-spot)" />
						{circleMark(cx, cy, rx, ry, draw, grow, circleOpacity, color)}
					</g>
				);
			}
			if (circleStyle === "highlighter") {
				const [a, b] = R(1);
				const pts = partial(scribbleLoop(cx, cy, a, b, -9, circleSeed), draw);
				return (
					<g opacity={circleOpacity}>
						<path d={markerStroke(pts, px(46))} fill="#ffe14d" opacity={0.62} />
						<path d={markerStroke(pts, px(14))} fill="#fff27a" opacity={0.5} />
					</g>
				);
			}
			if (circleStyle === "neon") {
				const [a, b] = R(1);
				const pts = partial(scribbleLoop(cx, cy, a, b, -9, circleSeed), draw);
				const d = markerStroke(pts, px(12));
				const glowColor = config.circleColor ?? "#5bffc8";
				return (
					<g opacity={circleOpacity}>
						<path d={markerStroke(pts, px(34))} fill={glowColor} opacity={0.55} style={{ filter: `blur(${px(14)}px)` }} />
						<path d={d} fill={glowColor} />
						<path d={markerStroke(pts, px(5))} fill="#ffffff" />
					</g>
				);
			}
			return circleMark(cx, cy, rx, ry, draw, grow, circleOpacity, color);
		})();
		return (
			<AbsoluteFill>
				<svg width={width} height={height} style={{ position: "absolute", inset: 0 }}>
					{circle}
					{noteOpacity > 0.01 ? (
						<g opacity={noteOpacity}>
							{arrow > 0.02 ? <path d={markerStroke(curve, px(9))} fill={color} /> : null}
							{arrow > 0.95 ? (
								<path d={`M${head(0.55).join(" ")} L${end.join(" ")} L${head(-0.55).join(" ")}`} fill="none" stroke={color} strokeWidth={px(7)} strokeLinecap="round" strokeLinejoin="round" />
							) : null}
							<g opacity={words} transform={caps ? undefined : `rotate(-5 ${textX} ${lastY})`}>
								{lines.map((line, i) => (
									<text
										key={i}
										x={textX}
										y={lastY - (lines.length - 1 - i) * lineH}
										fontFamily={caps ? "Poppins" : handwriting}
										fontWeight={700}
										fontSize={size}
										letterSpacing={caps ? px(1) : 0}
										fill={noteColor}
										stroke={caps ? "rgba(0,0,0,0.35)" : "#fff"}
										strokeWidth={caps ? px(5) : px(11)}
										paintOrder="stroke"
										strokeLinejoin="round"
										textAnchor="middle"
									>
										{caps ? line.toUpperCase() : line}
									</text>
								))}
							</g>
						</g>
					) : null}
				</svg>
			</AbsoluteFill>
		);
	}

	if (style === "circle-zoom") {
		const [cx, cy] = stopsXY[0] || [width / 2, height / 2];
		const rx = px(118);
		const ry = px(84);
		const draw = interpolate(frame, CZ_CIRCLE, [0, 1], { ...clamp, easing: Easing.inOut(Easing.sin) });
		const note = interpolate(frame, CZ_NOTE, [0, 1], { ...clamp, easing: Easing.out(Easing.cubic) });
		// The circle rides the zoom but more slowly than the ground, so the window onto the clip opens
		// over the zoom instead of snapping shut in its first frames.
		const grow = frame < CZ_ZOOM[0] ? 1 : Math.pow(circleHeld / czAltitude(Math.min(frame, CZ_ZOOM[1])), 0.34);
		const zoomed = interpolate(frame, CZ_ZOOM, [0, 1], clamp);
		const windowIn = interpolate(frame, CZ_WINDOW, [0, 1], { ...clamp, easing: Easing.out(Easing.cubic) });
		const full = frame >= CZ_ZOOM[1];
		const loop = scribbleLoop(cx, cy, rx * grow, ry * grow, -9, circleSeed);
		const windowPath = loop.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ") + " Z";

		// The ending is clip-pin's: the clip shrinks into the first stop's pin.
		const [sx, sy] = project(stop);
		const s = interpolate(frame, CZ_SHRINK, [0, 1], { ...clamp, easing: Easing.inOut(Easing.cubic) });
		const r = interpolate(s, [0, 1], [Math.hypot(width, height) / 2 + 40, px(56)]);
		const pcx = width / 2 + (sx - width / 2) * s;
		const pcy = height / 2 + (sy - height / 2) * s;
		const land = interpolate(frame, [introFrames, introFrames + introOverhang("circle-zoom")], [1, 0], { ...clamp, easing: Easing.in(Easing.cubic) });
		const ring = interpolate(s, [0.55, 0.9], [0, 1], clamp);

		return (
			<AbsoluteFill>
				{!full ? mapScrim(1 - windowIn) : null}
				{windowIn > 0 ? (
					<AbsoluteFill
						style={{
							clipPath: full ? `circle(${r * land}px at ${pcx}px ${pcy}px)` : `path('${windowPath}')`,
							opacity: full ? (land < 1 ? Math.min(1, land * 1.6) : 1) : windowIn,
						}}
					>
						<AbsoluteFill style={{ transform: `scale(${1.04 + 0.1 * s})` }}>{clip}</AbsoluteFill>
						<AbsoluteFill style={{ opacity: 1 - s }}>{scrim}</AbsoluteFill>
					</AbsoluteFill>
				) : null}
				{full ? (
					<div
						style={{
							position: "absolute",
							left: pcx - r * land,
							top: pcy - r * land,
							width: r * land * 2,
							height: r * land * 2,
							borderRadius: "50%",
							boxSizing: "border-box",
							border: `${px(8)}px solid #fff`,
							boxShadow: `0 ${px(10)}px ${px(30)}px rgba(15,62,74,.35)`,
							opacity: ring * land,
						}}
					/>
				) : null}
				<svg width={width} height={height} style={{ position: "absolute", inset: 0 }}>
					{!full ? circleMark(cx, cy, rx, ry, draw, grow, 1 - interpolate(zoomed, [0.55, 0.9], [0, 1], clamp)) : null}
					{!full ? circleNote(cx, cy, rx, ry, note, 1 - interpolate(zoomed, [0, 0.25], [0, 1], clamp)) : null}
				</svg>
				{words}
			</AbsoluteFill>
		);
	}

	if (style === "circle-match") {
		const cx = circleAt.x * width;
		const cy = circleAt.y * height;
		const rx = px(150);
		const ry = px(104);
		const draw = interpolate(frame, CM_CIRCLE, [0, 1], { ...clamp, easing: Easing.inOut(Easing.sin) });
		const note = interpolate(frame, CM_NOTE, [0, 1], { ...clamp, easing: Easing.out(Easing.cubic) });
		const cut = interpolate(frame, CM_CUT, [0, 1], { ...clamp, easing: Easing.inOut(Easing.quad) });
		const out = interpolate(frame, CM_OUT, [0, 1], clamp);
		const ghost = interpolate(frame, CM_GHOST, [1, 0], clamp);
		// After the cut the circle stays round the stop and shrinks with the map, more slowly than the
		// ground does, until the stop's own dot takes over from it.
		const shrink = frame < CM_OUT[0] ? 1 : Math.pow(circleClose / cmAltitude(Math.min(frame, CM_OUT[1])), 0.3);
		const [ccx, ccy] = frame < CM_OUT[0] ? [cx, cy] : [fx, fy];
		const circleFade = 1 - interpolate(out, [0.7, 1], [0, 1], clamp);
		const outlineIn = interpolate(frame, CM_OUTLINE, [0, 1], { ...clamp, easing: Easing.inOut(Easing.cubic) });
		const ringsXY = rings.map((ring) => smoothed(place(ring), 4));
		const countryD = ringsXY.map((ring) => pathOf(ring, true)).join(" ");
		const Lc = Math.max(1, ...ringsXY.map(len));

		return (
			<AbsoluteFill>
				{mapScrim(cut * (1 - wordsOut))}
				{cut < 1 ? (
					<AbsoluteFill style={{ opacity: 1 - cut, transform: `scale(${1 + 0.08 * cut})` }}>
						{clip}
						{scrim}
					</AbsoluteFill>
				) : null}
				<svg width={width} height={height} style={{ position: "absolute", inset: 0 }}>
					{countryD && outlineIn > 0 ? (
						<path
							d={countryD}
							fill="none"
							stroke="#fff"
							strokeOpacity={0.85 * ghost}
							strokeWidth={px(4)}
							strokeLinejoin="round"
							strokeDasharray={Lc}
							strokeDashoffset={Lc * (1 - outlineIn)}
						/>
					) : null}
					<g opacity={ghost}>
						{dottedRoute("circle-reveal")}
						{laterStops()}
					</g>
					{pulse(CM_DIVE)}
					{pulse(CM_DIVE + 22)}
					{pulse(introFrames - 12)}
					{frame >= CM_OUT[0] ? firstStop() : null}
					{vehicleTile()}
					{circleMark(ccx, ccy, rx, ry, draw, shrink, circleFade)}
					{circleNote(ccx, ccy, rx * shrink, ry * shrink, note, 1 - interpolate(out, [0.2, 0.5], [0, 1], clamp))}
				</svg>
				{words}
			</AbsoluteFill>
		);
	}

	// ---- clip-pin ----------------------------------------------------------------------------------
	const [sx, sy] = project(stop);
	const s = interpolate(frame, [SHRINK_IN, SHRINK_OUT], [0, 1], { ...clamp, easing: Easing.inOut(Easing.cubic) });
	const pinR = px(56);
	const r = interpolate(s, [0, 1], [Math.hypot(width, height) / 2 + 40, pinR]);
	const cx = width / 2 + (sx - width / 2) * s;
	const cy = height / 2 + (sy - height / 2) * s;
	// Once it has landed, the film pin gives way to the real one springing up beneath it.
	const land = interpolate(frame, [introFrames, introFrames + introOverhang("clip-pin")], [1, 0], { ...clamp, easing: Easing.in(Easing.cubic) });
	const ring = interpolate(s, [0.55, 0.9], [0, 1], clamp);

	// The coordinates of the first stop, counting up to where it is -- the clip is being placed on the map.
	const count = interpolate(frame, [6, 38], [0, 1], { ...clamp, easing: Easing.out(Easing.cubic) });
	const fmt = (v: number, pos: string, neg: string) => `${Math.abs(v * count).toFixed(4)}° ${v >= 0 ? pos : neg}`;
	const chipIn = spring({ frame: frame - 4, fps, config: { damping: 15, stiffness: 130 } });
	const city = currentTrip?.destinations?.[0]?.cityEnglish || currentTrip?.destinations?.[0]?.normalised_city || "";

	return (
		<AbsoluteFill>
			<AbsoluteFill
				style={{
					clipPath: `circle(${r * land}px at ${cx}px ${cy}px)`,
					opacity: land < 1 ? Math.min(1, land * 1.6) : 1,
				}}
			>
				<AbsoluteFill style={{ transform: `scale(${1.04 + 0.1 * s})` }}>{clip}</AbsoluteFill>
				<AbsoluteFill style={{ opacity: 1 - s }}>{scrim}</AbsoluteFill>
			</AbsoluteFill>
			{/* The ring is what makes the shrinking film read as a pin rather than a vignette. */}
			<div
				style={{
					position: "absolute",
					left: cx - r * land,
					top: cy - r * land,
					width: r * land * 2,
					height: r * land * 2,
					borderRadius: "50%",
					boxSizing: "border-box",
					border: `${px(8)}px solid #fff`,
					boxShadow: `0 ${px(10)}px ${px(30)}px rgba(0,0,0,.35)`,
					opacity: ring * land,
				}}
			/>

			{words}

			<div
				style={{
					position: "absolute",
					left: px(72),
					// Under the words: a third of the way down a portrait frame, near the foot of a landscape one.
					top: Math.min(px(1250), height - px(220)),
					display: "flex",
					alignItems: "center",
					gap: px(22),
					padding: `${px(22)}px ${px(34)}px ${px(22)}px ${px(24)}px`,
					borderRadius: px(999),
					background: "rgba(255,255,255,.94)",
					boxShadow: `0 ${px(8)}px ${px(30)}px rgba(0,0,0,.25)`,
					fontFamily: "Poppins",
					opacity: chipIn * (1 - Math.min(1, s * 2.2)),
					transform: `translateY(${px(40) * (1 - chipIn)}px) scale(${0.92 + 0.08 * chipIn})`,
					transformOrigin: "left center",
				}}
			>
				<svg width={px(54)} height={px(66)} viewBox="0 0 54 66">
					<path d="M27 64C27 64 52 40 52 25A25 25 0 0 0 2 25C2 40 27 64 27 64Z" fill={brandColor} />
					<circle cx="27" cy="25" r="10" fill="#fff" />
				</svg>
				<div>
					<div style={{ fontSize: px(44), fontWeight: 700, color: "#0f3e4a", lineHeight: 1.05 }}>{city}</div>
					<div style={{ fontSize: px(26), fontWeight: 500, color: "#0f3e4a", opacity: 0.7, fontVariantNumeric: "tabular-nums", letterSpacing: px(1) }}>
						{fmt(stop.lat, "N", "S")} {fmt(stop.lng, "E", "W")}
					</div>
				</div>
			</div>
		</AbsoluteFill>
	);
};

export default ClipIntro;
